import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Container,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Dashboard as DashboardIcon,
  FilterAlt as FilterAltIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import { useRole } from '../../contexts/RoleContext';
import DashboardService from '../../services/DashboardService';
import MetricsCard from '../../components/Dashboard/MetricsCard';
import StatusPieChart from '../../components/Dashboard/StatusPieChart';
import CountryBarChart from '../../components/Dashboard/CountryBarChart';
import OEBarChart from '../../components/Dashboard/OEBarChart';
import StageCycleTimeContent from './StageCycleTimeContent';

const Dashboard = () => {
  const userRole = useRole();
  const statusChartRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedODMType, setSelectedODMType] = useState('Bandwidth Upgrade/Downgrade');
  const [selectedOE, setSelectedOE] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [dashboardData, setDashboardData] = useState({
    metrics: {},
    statusDistribution: [],
    countryDistribution: [],
    oeDistribution: []
  });

  const odmTypes = [
    'Bandwidth Upgrade/Downgrade',
  ];

  // Full reload when ODM type changes (also resets OE filter)
  useEffect(() => {
    setSelectedOE(null);
    loadDashboardData(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedODMType]);

  // Re-fetch only the status donut whenever OE selection changes
  const fetchStatusDistribution = useCallback(async (oeName) => {
    setLoadingStatus(true);
    try {
      const statusDist = await DashboardService.getStatusDistribution(selectedODMType, oeName || '');
      setDashboardData((prev) => ({ ...prev, statusDistribution: statusDist }));
    } catch (err) {
      console.error('Error fetching status distribution:', err);
    } finally {
      setLoadingStatus(false);
    }
  }, [selectedODMType]);

  // Scroll to status chart after selectedOE state has been applied to DOM
  useEffect(() => {
    if (selectedOE === null) return;
    fetchStatusDistribution(selectedOE);
    const timer = setTimeout(() => {
      if (statusChartRef.current) {
        statusChartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOE]);

  const loadDashboardData = async (oeOverride) => {
    try {
      setLoading(true);
      setError(null);

      const oe = oeOverride !== undefined ? oeOverride : selectedOE;

      const [metrics, statusDist, countryDist, oeDist] = await Promise.all([
        DashboardService.getDashboardMetrics(selectedODMType),
        DashboardService.getStatusDistribution(selectedODMType, oe || ''),
        DashboardService.getCountryDistribution(selectedODMType),
        DashboardService.getOEDistribution(selectedODMType)
      ]);

      setDashboardData({
        metrics,
        statusDistribution: statusDist,
        countryDistribution: countryDist,
        oeDistribution: oeDist
      });
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setSelectedOE(null);
    loadDashboardData(null);
  };

  // When OE is cleared (Reset to All), re-fetch without filter
  const handleResetOE = () => {
    setSelectedOE(null);
    fetchStatusDistribution(null);
  };

  const handleOEClick = (oeName) => {
    setSelectedOE(oeName);
    // fetchStatusDistribution + scroll are triggered by the useEffect above
  };

  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          flexDirection="column"
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          sx={{ backgroundColor: '#f5f5f5' }}
        >
          <CircularProgress size={60} sx={{ color: '#003781', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#003781' }}>
            Loading Dashboard...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Tab bar */}
        <Box sx={{ borderBottom: '1px solid #dce3f0', background: '#fff' }}>
          <Container maxWidth="xl">
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                  color: '#6b7280',
                  minHeight: 52,
                  '&.Mui-selected': { color: '#003781' },
                },
                '& .MuiTabs-indicator': { backgroundColor: '#003781', height: 3 },
              }}
            >
              <Tab icon={<DashboardIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Overview" />
              <Tab icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="SLA Tracker" />
            </Tabs>
          </Container>
        </Box>

        {/* Tab 0: Overview (existing dashboard) */}
        {activeTab === 0 && (
        <Box sx={{ py: 3 }}>
            <Container maxWidth="xl">
              {/* Header Section */}
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: 'white'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <DashboardIcon sx={{ fontSize: 40, color: '#003781', mr: 2 }} />
                    <Box>
                      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#003781' }}>
                        ODM Analytics Dashboard
                      </Typography>
                      <Box display="flex" alignItems="center" mt={1}>
                        <Chip
                          label={userRole.roleName}
                          color={userRole.isAdmin ? "error" : "success"}
                          size="small"
                          sx={{ mr: 2 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {userRole.userEmail} • Last updated: {lastRefresh.toLocaleTimeString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={2}>
                    <FormControl variant="outlined" sx={{ minWidth: 280 }}>
                      <InputLabel>ODM Request Type</InputLabel>
                      <Select
                        value={selectedODMType}
                        onChange={(e) => setSelectedODMType(e.target.value)}
                        label="ODM Request Type"
                      >
                        {odmTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            <AssessmentIcon sx={{ mr: 1, fontSize: 20 }} />
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Tooltip title="Refresh Data">
                      <IconButton
                        onClick={handleRefresh}
                        sx={{
                          bgcolor: '#003781',
                          color: 'white',
                          '&:hover': { bgcolor: '#002561' }
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>

              {/* Error Display */}
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  <Typography fontWeight="bold">Error loading dashboard data:</Typography>
                  {error}
                </Alert>
              )}

              {/* Metrics Cards Row */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {[
                  { title: "Total Requests", value: dashboardData.metrics.total || 0, color: "primary" },
                  { title: "Pending Review", value: dashboardData.metrics.pending || 0, color: "warning" },
                  { title: "In Progress", value: dashboardData.metrics.inProgress || 0, color: "info" },
                  { title: "Completed", value: dashboardData.metrics.completed || 0, color: "success" }
                ].map((metric, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <MetricsCard
                      title={metric.title}
                      value={metric.value}
                      color={metric.color}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Charts Section */}
              <Grid container spacing={3}>
                {/* Status Distribution Chart */}
                <Grid item xs={12}>
                  <Paper
                    ref={statusChartRef}
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Active OE filter banner */}
                    {selectedOE && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 3,
                          py: 1.25,
                          background: 'linear-gradient(90deg, #003781 0%, #0057A8 100%)',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <FilterAltIcon sx={{ color: '#fff', fontSize: 18 }} />
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>
                            Filtered by OE:
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                            {selectedOE}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                          onClick={handleResetOE}
                          sx={{
                            color: '#fff',
                            borderColor: 'rgba(255,255,255,0.5)',
                            border: '1px solid',
                            textTransform: 'none',
                            fontSize: 12,
                            px: 1.5,
                            py: 0.4,
                            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
                          }}
                        >
                          Reset to All
                        </Button>
                      </Box>
                    )}

                    <Box sx={{ p: 3, pb: 1 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <TrendingUpIcon sx={{ color: '#003781', fontSize: 24 }} />
                          <Typography variant="h6" fontWeight="bold" color="#003781">
                            Request Status Distribution
                          </Typography>
                        </Box>
                        {loadingStatus && <CircularProgress size={18} sx={{ color: '#003781' }} />}
                      </Box>
                      <Divider />
                    </Box>
                    <Box sx={{ height: 460, p: 2 }}>
                      <StatusPieChart data={dashboardData.statusDistribution} />
                    </Box>
                  </Paper>
                </Grid>

                {/* Country Distribution Chart */}
                <Grid item xs={12}>
                  <Paper
                    elevation={2}
                    sx={{
                      height: 500,
                      borderRadius: 2,
                      backgroundColor: 'white',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ p: 3, pb: 1 }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Typography sx={{ fontSize: '24px', mr: 1 }}></Typography>
                        <Typography variant="h6" fontWeight="bold" color="#003781">
                          Geographic Distribution
                        </Typography>
                      </Box>
                      <Divider />
                    </Box>
                    <Box sx={{ flex: 1, p: 2 }}>
                      <CountryBarChart data={dashboardData.countryDistribution} />
                    </Box>
                  </Paper>
                </Grid>

                {/* OE Distribution Chart */}
                <Grid item xs={12}>
                  <Paper
                    elevation={2}
                    sx={{
                      height: 700,
                      borderRadius: 2,
                      backgroundColor: 'white',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ p: 3, pb: 1 }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Typography sx={{ fontSize: '24px', mr: 1 }}></Typography>
                        <Typography variant="h6" fontWeight="bold" color="#003781">
                          Organizational Entity Distribution
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
                          — click a bar to filter the status chart above
                        </Typography>
                      </Box>
                      <Divider />
                    </Box>
                    <Box sx={{ flex: 1, p: 2 }}>
                      <OEBarChart
                        data={dashboardData.oeDistribution}
                        onOEClick={handleOEClick}
                        selectedOE={selectedOE}
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
          </Box>
        )}

        {/* Tab 1: SLA Tracker */}
        {activeTab === 1 && <StageCycleTimeContent />}
      </Box>
    </Layout>
  );
};

export default Dashboard;